// src/frontend/src/App.js
import "./App.css";
import React from "react";
import { BrowserRouter as Router, Route, Routes, Navigate } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { ManufacturerPage } from "./pages/ManufacturerPage";
import { DatePage } from "./pages/DatePage";
import { SkuPage } from "./pages/SkuPage";
import { HomePage } from "./pages/HomePage";
import { MyTrackedStocksPage } from "./pages/MyTrackedStocksPage";
import Navbar from "./navbar/Navbar";
import ScrollToTop from "./components/ScrollToTop";
import { Box, CircularProgress } from "@mui/material";
import SubscriptionPage from "./pages/SubscriptionPage";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { AppThemeProvider } from "./context/ThemeContext";
import LandingPage from "./pages/LandingPage";
import OAuth2RedirectHandler from "./OAuth2RedirectHandler";
import ProfilePage from "./pages/ProfilePage";
import AdminPage from "./pages/AdminPage";
import OnboardingDialog from "./components/OnboardingDialog";
import FreeTrialPopup from "./components/FreeTrialPopup";
import PrivacyPage from "./pages/PrivacyPage";
import UnsubscribePage from "./pages/UnsubscribePage";
import StatusPage from "./pages/StatusPage";
import OtpSetupPage from "./pages/OtpSetupPage";
import TermsPage from "./pages/TermsPage";
import RefundPage from "./pages/RefundPage";
import SupportPage from "./pages/SupportPage";
import { TrackHubPage } from "./pages/TrackHubPage";
import { BlogIndexPage } from "./pages/BlogIndexPage";
import { BlogArticlePage } from "./pages/BlogArticlePage";
import RouteSeoDefaults from "./components/RouteSeoDefaults";

function AppContent() {
    const { loading, needsOnboarding, completeOnboarding } = useAuth();

    if (loading) {
        return (
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    minHeight: '100vh'
                }}
            >
                <CircularProgress />
            </Box>
        );
    }

    return (
        <>
            <RouteSeoDefaults />
            <ScrollToTop />
            <Navbar />
            <Routes>
                <Route path="/" element={<LandingPage />} />
                <Route path="/skus/:sku" element={<SkuPage />} />
                <Route path="/manufacturers/:stockManufacturer" element={<ManufacturerPage />} />
                <Route path="/dates" element={<DatePage />} />
                <Route path="/stocks" element={<HomePage />} />
                <Route path="/deals" element={<Navigate to="/stocks" replace />} />
                <Route path="/track/:trackSlug" element={<TrackHubPage />} />
                <Route path="/blog" element={<BlogIndexPage />} />
                <Route path="/blog/:articleSlug" element={<BlogArticlePage />} />
                <Route path="/my-tracked-stocks" element={<MyTrackedStocksPage />} />
                <Route path="/subscription" element={<SubscriptionPage />} />
                <Route path="/subscription/success" element={<SubscriptionPage />} />
                <Route path="/subscription/canceled" element={<SubscriptionPage />} />
                <Route path="/profile" element={<ProfilePage />} />
                <Route path="/admin" element={<AdminPage />} />
                <Route path="/privacy" element={<PrivacyPage />} />
                <Route path="/unsubscribe" element={<UnsubscribePage />} />
                <Route path="/status" element={<StatusPage />} />
                <Route path="/docs/otp-autofill" element={<OtpSetupPage />} />
                <Route path="/terms" element={<TermsPage />} />
                <Route path="/refund" element={<RefundPage />} />
                <Route path="/support" element={<SupportPage />} />
                <Route path="/oauth2/redirect" element={<OAuth2RedirectHandler />} />
                <Route path="/login/oauth2/code/google" element={<Navigate to="/" />} />
                <Route path="*" element={<Navigate to="/" />} />
            </Routes>

            <OnboardingDialog
                open={needsOnboarding}
                onContinue={completeOnboarding}
            />

            <FreeTrialPopup />
        </>
    );
}

function App() {
    return (
        <AppThemeProvider>
            <AuthProvider>
                <HelmetProvider>
                    <div className="App">
                        <Router future={{
                            v7_startTransition: true,
                            v7_relativeSplatPath: true
                        }}>
                            <AppContent />
                        </Router>
                    </div>
                </HelmetProvider>
            </AuthProvider>
        </AppThemeProvider>
    );
}

export default App;
